import React, { useEffect, useRef } from "react";

/**
 * Compass component that shows N/S direction and rotates based on camera orientation
 * Uses refs for performant rotation updates without re-renders
 */
const Compass = ({ controls, arrowsRotation = 0, colors = { primary: "#ffffff" } }) => {
  const compassRef = useRef(null);
  const animationFrameRef = useRef(null);

  useEffect(() => {
    if (!controls) return;

    const updateCompassRotation = () => {
      if (compassRef.current && controls) {
        // Get the camera's azimuthal angle (horizontal rotation around Y axis)
        // azimuth = 0 means camera is on +Z axis looking towards origin
        const azimuthAngle = controls.getAzimuthalAngle();
        
        // Apply the arrows rotation offset (divided by 10 as per project convention)
        // This rotates the whole arrows group, shifting where North actually is
        const arrowsOffset = (arrowsRotation || 0) / 10;
        
        // Convert to degrees
        const azimuthDeg = azimuthAngle * (180 / Math.PI);
        const offsetDeg = arrowsOffset * (180 / Math.PI);
        
        // North is at +X direction from center
        // When camera azimuth = 0 (on +Z looking at origin), North (+X) is 90° to the right
        // When camera azimuth = π/2 (on -X looking at origin/+X), camera looks at North, so N is at top (0°)
        // Add 180 to flip the compass so N points correctly
        const rotationDeg = -(90 - azimuthDeg) - offsetDeg + 180;
        
        compassRef.current.style.transform = `rotate(${rotationDeg}deg)`;
      }
      
      animationFrameRef.current = requestAnimationFrame(updateCompassRotation);
    };

    // Start the animation loop
    animationFrameRef.current = requestAnimationFrame(updateCompassRotation);

    // Cleanup
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [controls, arrowsRotation]);

  const size = 60;
  const borderWidth = 1.5;
  const labelShadow = "0 1px 3px rgba(0, 0, 0, 0.55)";

  return (
    <div
      style={{
        position: "fixed",
        bottom: "13px",
        left: "60px",
        width: size,
        height: size,
        zIndex: 1000,
        pointerEvents: "none",
        filter: "drop-shadow(0 2px 6px rgba(0, 0, 0, 0.35))",
      }}
    >
      {/* Rotating compass ring - no solid backdrop, sits directly on the model */}
      <div
        ref={compassRef}
        style={{
          width: "100%",
          height: "100%",
          borderRadius: "50%",
          background: "transparent",
          border: `${borderWidth}px solid ${colors.primary}`,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "6px 0",
          boxSizing: "border-box",
        }}
      >
        {/* North label */}
        <span
          style={{
            color: colors.primary,
            fontSize: "14px",
            fontWeight: "bold",
            lineHeight: 1,
            userSelect: "none",
            textShadow: labelShadow,
          }}
        >
          N
        </span>

        {/* Center needle/indicator */}
        <div
          style={{
            width: "2px",
            height: "16px",
            background: `linear-gradient(to bottom, ${colors.primary} 50%, rgba(255,255,255,0.5) 50%)`,
            borderRadius: "1px",
          }}
        />

        {/* South label */}
        <span
          style={{
            color: "rgba(255, 255, 255, 0.85)",
            fontSize: "12px",
            fontWeight: "normal",
            lineHeight: 1,
            userSelect: "none",
            textShadow: labelShadow,
          }}
        >
          S
        </span>
      </div>
    </div>
  );
};

export default Compass;
